// MainLayout — MerakiPeople-branded shell.
// Indigo sidebar, coral CTA, iconography forward. Designed for HR / owners:
// bold typography, generous whitespace, clear call-to-action hierarchy.

import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Plus,
  LogOut,
  Building2,
  Briefcase,
  Sparkles,
  ShieldCheck,
  Users,
  Settings,
} from "lucide-react";
import { authService } from "../../services/auth.service";
import PlaceOSLogo from "../common/PlaceOSLogo";

const MainLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActiveRoute = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isCreateJobPage = location.pathname === "/jobs/create";

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = user.role;

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  // Avatar initial / fallback chip
  const initials = (user.companyName || user.name || "MP")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <div className="min-h-screen bg-meraki-cloud flex font-outfit text-meraki-graphite">
      {/* ── Sidebar ── */}
      <aside className="w-[260px] bg-meraki-indigo text-white flex flex-col shadow-meraki-card">
        {/* Brand block */}
        <div className="p-5 border-b border-white/10">
          <PlaceOSLogo size="compact" />
        </div>

        {/* Primary CTA — Create New JD */}
        <div className="px-4 pt-5">
          {!isCreateJobPage && userRole !== "admin" && (
            <Link
              to="/jobs/create"
              className="w-full flex items-center justify-center gap-2 bg-meraki-coral hover:bg-meraki-coral-600 text-white font-bold py-3 rounded-meraki shadow-meraki-glow transition active:scale-[0.98]"
            >
              <Plus size={18} strokeWidth={3} />
              <span>Create new JD</span>
            </Link>
          )}
        </div>

        {/* Nav */}
        <nav className="px-3 py-5 flex-1 space-y-1.5 text-sm font-medium">
          <NavItem
            to={userRole === "admin" ? "/admin/dashboard" : "/dashboard"}
            icon={LayoutDashboard}
            label="Dashboard"
            active={isActiveRoute("/dashboard") || isActiveRoute("/admin/dashboard")}
          />
          <NavItem to="/jobs"         icon={Briefcase} label="Jobs"         active={isActiveRoute("/jobs")} />
          {userRole !== "admin" && (
            <>
              <NavItem to="/companies"  icon={Building2} label="Companies"  active={isActiveRoute("/companies")} />
              <NavItem to="/talent-pool" icon={Users}    label="Talent Pool" active={isActiveRoute("/talent-pool")} />
            </>
          )}
          {userRole === "admin" && (
            <NavItem to="/admin/tokens" icon={Sparkles} label="Tokens" active={isActiveRoute("/admin/tokens")} />
          )}
          {userRole !== "admin" && (
            <NavItem to="/settings" icon={Settings} label="Settings" active={isActiveRoute("/settings")} />
          )}
        </nav>

        {/* User card + logout */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold">
              {(user.name || user.email || "U")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user.name || user.email || "User"}</div>
              <div className="text-xs text-white/60 capitalize flex items-center gap-1">
                {userRole === "admin" && <ShieldCheck size={12} />}
                {userRole || "member"}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-meraki text-white/80 hover:bg-white/10 hover:text-white transition"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
};

function NavItem({ to, icon: Icon, label, active }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-meraki transition group ${
        active
          ? "bg-white text-meraki-indigo font-bold shadow-meraki-soft"
          : "text-white/80 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon size={20} strokeWidth={active ? 2.4 : 2} />
      <span>{label}</span>
    </Link>
  );
}

export default MainLayout;
